import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RefreshCw, FileText, Filter, Calendar, MapPin, ChevronDown, ChevronUp, Search, Download } from 'lucide-react';
import { getChallans, getChallanStats } from '../services/api';
import { Challan } from '../types';
import { formatDate } from '../utils/dates';
import clsx from 'clsx';

export default function Challans() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [challans, setChallans] = useState<Challan[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('challanDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [challansRes, statsRes] = await Promise.all([
        getChallans({ limit: 200 }),
        getChallanStats()
      ]);
      if (challansRes.success && challansRes.data) setChallans(challansRes.data);
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Client-side filtering
  const filteredChallans = challans.filter(c => {
    if (statusFilter && c.challanStatus !== statusFilter) return false;
    if (stateFilter && c.stateCode !== stateFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return c.challanNumber?.toLowerCase().includes(term) ||
             c.vehicleNumber?.toLowerCase().includes(term) ||
             c.challanPlace?.toLowerCase().includes(term);
    }
    return true;
  });

  // Sorting
  const sortedChallans = [...filteredChallans].sort((a, b) => {
    let aVal: any, bVal: any;
    if (sortField === 'challanDate') {
      aVal = (a.challanDate as any)?._seconds || 0;
      bVal = (b.challanDate as any)?._seconds || 0;
    } else if (sortField === 'challanAmount') {
      aVal = a.challanAmount || 0;
      bVal = b.challanAmount || 0;
    } else if (sortField === 'vehicleNumber') {
      aVal = a.vehicleNumber || '';
      bVal = b.vehicleNumber || '';
    } else {
      aVal = (a as any)[sortField] || '';
      bVal = (b as any)[sortField] || '';
    }
    if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-4 h-4 text-primary-600" /> : <ChevronDown className="w-4 h-4 text-primary-600" />;
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      PENDING: 'bg-amber-50 text-amber-700 border border-amber-200',
      PAID: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      DISPOSED: 'bg-blue-50 text-blue-700 border border-blue-200',
      DISPUTED: 'bg-red-50 text-red-700 border border-red-200',
    };
    return <span className={clsx('px-2.5 py-1 rounded-full text-xs font-semibold', styles[status] || 'bg-gray-100 text-gray-700')}>{status}</span>;
  };

  const uniqueStates = [...new Set(challans.map(c => c.stateCode).filter(Boolean))].sort();

  const exportCSV = () => {
    const headers = ['Challan No', 'Vehicle', 'Date', 'State', 'Amount', 'Status', 'Offences'];
    const rows = sortedChallans.map(c => [
      c.challanNumber,
      c.vehicleNumber,
      formatDate(c.challanDate),
      c.stateCode,
      c.challanAmount,
      c.challanStatus,
      c.offences?.map(o => o.offenceDescription).join('; ')
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'challans.csv';
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Challans</h2>
          <p className="text-sm text-gray-500 mt-1">
            {stats ? `${stats.total} total | ${stats.pending} pending | ₹${(stats.totalAmountPending || 0).toLocaleString()} due` : 'Loading...'}
          </p>
        </div>
        <button onClick={exportCSV} className="btn btn-secondary flex items-center gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <p className="text-sm font-medium text-gray-500">Total</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <p className="text-sm font-medium text-gray-500">Pending</p>
            <p className="text-3xl font-bold text-amber-600 mt-1">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <p className="text-sm font-medium text-gray-500">Amount Due</p>
            <p className="text-3xl font-bold text-red-600 mt-1">₹{(stats.totalAmountPending || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <p className="text-sm font-medium text-gray-500">Amount Paid</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">₹{(stats.totalAmountPaid || 0).toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search challan, vehicle, location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm font-medium bg-white"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="PAID">Paid</option>
              <option value="DISPOSED">Disposed</option>
            </select>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm font-medium bg-white"
            >
              <option value="">All States</option>
              {uniqueStates.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
        </div>
        {(statusFilter || stateFilter || searchTerm) && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">Showing {sortedChallans.length} of {challans.length}</span>
            <button onClick={() => { setStatusFilter(''); setStateFilter(''); setSearchTerm(''); }} className="text-sm text-primary-600 hover:underline ml-2">
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : sortedChallans.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No challans found</h3>
          <p className="text-gray-500">Try adjusting your filters or add vehicles to fetch challans</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('challanNumber')}>
                    <div className="flex items-center gap-1">Challan <SortIcon field="challanNumber" /></div>
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('vehicleNumber')}>
                    <div className="flex items-center gap-1">Vehicle <SortIcon field="vehicleNumber" /></div>
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('challanDate')}>
                    <div className="flex items-center gap-1">Date <SortIcon field="challanDate" /></div>
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-6 py-4">Offences</th>
                  <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider px-6 py-4 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('challanAmount')}>
                    <div className="flex items-center justify-end gap-1">Amount <SortIcon field="challanAmount" /></div>
                  </th>
                  <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedChallans.map((challan, idx) => (
                  <tr key={challan.challanNumber} className={clsx('hover:bg-gray-50 transition-colors', idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30')}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-gray-900 text-sm">{challan.challanNumber}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{challan.stateCode}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Link to={`/vehicles/${challan.vehicleNumber}`} className="font-semibold text-primary-600 hover:text-primary-700 text-sm hover:underline">
                        {challan.vehicleNumber}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-sm text-gray-700">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {formatDate(challan.challanDate)}
                      </div>
                      {challan.challanPlace && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                          <MapPin className="w-3 h-3" />
                          {challan.challanPlace}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5 max-w-xs">
                        {challan.offences?.slice(0, 2).map((off, i) => (
                          <span key={i} className="inline-flex text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-md">
                            {off.offenceDescription?.slice(0, 25)}{(off.offenceDescription?.length || 0) > 25 ? '...' : ''}
                          </span>
                        ))}
                        {(challan.offences?.length || 0) > 2 && (
                          <span className="text-xs text-gray-400 px-1">+{challan.offences!.length - 2} more</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <p className="font-bold text-gray-900">₹{(challan.challanAmount || 0).toLocaleString()}</p>
                      {challan.isOverdue && (
                        <p className="text-xs text-red-600 font-medium mt-0.5">{challan.challanAgeDays}d overdue</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {statusBadge(challan.challanStatus)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
            Showing {sortedChallans.length} challans
          </div>
        </div>
      )}
    </div>
  );
}
